import { formatDate, formatMarketCap, formatMarketCapShort } from '../lib/format';

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/**
 * Time spent under the market-cap threshold, broken out stretch by stretch.
 *
 * A single total would hide the shape of it: 200 days under the line in one unbroken
 * run is a different company from 200 days spread over eight brief dips. Each run is
 * listed with both counts — trading days are what we observed, calendar days are the
 * span a reader means by "how long".
 */
export default function BelowThreshold({ ticker, summary, years }) {
  const { threshold, stretches, tradingDays, calendarDays, longest, lastDate, runsToLastClose, observedDays } = summary;
  const label = formatMarketCapShort(threshold);
  const last = stretches[stretches.length - 1];

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Time below {label} market cap</h2>
        <p className="card-note">
          closes under {label} across the {years}-year window
        </p>
      </div>

      {stretches.length === 0 ? (
        <div className="empty">
          {ticker} never closed below {label} during this period — its close-based market cap stayed
          at or above the line on all {plural(observedDays, 'trading day')} measured.
        </div>
      ) : (
        <>
          <div className="stats">
            <div className="stat">
              <p className="stat-label">Stretches below {label}</p>
              <p className="stat-value stat-hero">{stretches.length}</p>
              <p className="stat-meta">
                separated by at least one close back above {label}
              </p>
            </div>
            <div className="stat">
              <p className="stat-label">Total days below</p>
              <p className="stat-value">{plural(tradingDays, 'trading day')}</p>
              <p className="stat-meta">
                {plural(calendarDays, 'calendar day')} · {((tradingDays / observedDays) * 100).toFixed(0)}% of
                the {plural(observedDays, 'day')} measured
              </p>
            </div>
            <div className="stat">
              <p className="stat-label">Longest stretch</p>
              <p className="stat-value">{plural(longest.tradingDays, 'trading day')}</p>
              <p className="stat-meta">
                {formatDate(longest.start)} – {formatDate(longest.end)}
              </p>
            </div>
            {/* Dated, not present tense. The series ends at the last bar Massive has, which
                for a delisted or suspended ticker is not today — "currently below" would then
                be asserting something about the present out of years-old data. */}
            <div className="stat">
              <p className="stat-label">At the latest close</p>
              <p className="stat-value">{runsToLastClose ? `Below ${label}` : `At or above ${label}`}</p>
              <p className="stat-meta">
                {formatDate(lastDate)} ·{' '}
                {runsToLastClose ? `below since ${formatDate(last.start)}` : `last below ${formatDate(last.end)}`}
              </p>
            </div>
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Stretch</th>
                  <th>From</th>
                  <th>To</th>
                  <th className="num">Trading days</th>
                  <th className="num">Calendar days</th>
                  <th className="num">Low</th>
                </tr>
              </thead>
              <tbody>
                {stretches.map((s, i) => (
                  <tr key={s.start}>
                    <td className="mono">{i + 1}</td>
                    <td className="mono">{formatDate(s.start)}</td>
                    <td className="mono">
                      {formatDate(s.end)}
                      {runsToLastClose && i === stretches.length - 1 && (
                        <span className="badge badge-warn" style={{ marginLeft: 8 }}>to last close</span>
                      )}
                    </td>
                    <td className="num">{s.tradingDays.toLocaleString()}</td>
                    <td className="num">{s.calendarDays.toLocaleString()}</td>
                    <td className="num">{formatMarketCap(s.low.marketCap)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="card-foot">
        <p className="hint" style={{ margin: 0 }}>
          Measured on the daily close × the share count in effect that day. Trading days are days
          the market was open; calendar days are the span from the first such close to the last.
          Days before the earliest filed share count in the window carry no market cap and are
          counted in neither.
          {runsToLastClose && (
            <>
              {' '}The last stretch runs to {formatDate(lastDate)}, the most recent close available
              for {ticker} — which is today only if the stock is still trading.
            </>
          )}
        </p>
      </div>
    </section>
  );
}
